import { useSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import FlexControlledSelectSeparatedLabel from "../../../components/controlled/FlexControlledSelectSeparatedLabel";
import NewControlledTextFieldSeparatedLabel from "../../../components/controlled/FlexControlledTextFieldSeparatedLabel";

import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGetCountryCodesQuery } from "../../cargo/cargoApiSlice";
import { useAddCompanyAsContactMutation } from "../companiesApiSlice";

function formatDateWithLeadingZeros(date) {
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();

	return `${day}-${month}-${year}`;
}

const AddCompany = () => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [addCompany, { isLoading }] = useAddCompanyAsContactMutation();
	const { data: countryCodes, isLoading: isLoadingCountryCodes } =
		useGetCountryCodesQuery();
	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const [notesState, setNotesState] = useState([]);
	const [unsavedNotes, setUnsavedNotes] = useState([]);
	const [savingNotes, setSavingNotes] = useState({});

	const handleAppendNote = (e) => {
		e.preventDefault();
		const today = formatDateWithLeadingZeros(new Date());

		const newNote = {
			id: `note-${uuidv4()}`,
			date: today,
			value: "",
		};

		setUnsavedNotes([...unsavedNotes, newNote]);
	};
	const handleNoteChange = (id, newValue) => {
		setUnsavedNotes((prevNotes) =>
			prevNotes.map((note) =>
				note.id === id ? { ...note, value: newValue } : note
			)
		);
	};

	const handleNoteSave = (id) => {
		setSavingNotes((prevSavingNotes) => ({ ...prevSavingNotes, [id]: true }));
		setNotesState((prevNotesState) => [
			{
				date: formatDateWithLeadingZeros(new Date()),
				value: unsavedNotes.find((note) => note.id === id).value,
			},
			...prevNotesState,
		]);
		setUnsavedNotes((prevNotes) => prevNotes.filter((note) => note.id !== id));
		setSavingNotes((prevSavingNotes) => {
			const newSavingNotes = { ...prevSavingNotes };
			delete newSavingNotes[id];
			return newSavingNotes;
		});
	};

	const onSubmit = async ({
		name,
		nip,
		country,
		postalCode,
		city,
		street,
		contactPersons,
	}) => {
		await addCompany({
			name,
			nip,
			country,
			postalCode,
			city,
			street,
			contactPersons,
			notesState,
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("added_company_as_contact"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container columnSpacing={5} rowSpacing={1} sx={{
                alignItems: "flex-start"
            }}>
				<Grid
					container
					rowSpacing={1}
					columnSpacing={2}
					size={{
						lg: 6,
					}}
				>
					<Grid size={12}>
						<Box
							sx={{
								display: "flex",
								gap: "10px",
								alignItems: "center",
								width: "100%",
							}}
						>
							<Typography sx={{ flex: "0" }}>{t("status")}: </Typography>
							<Box
								sx={{
									background: "#FFE5C9",
									py: 1,
									px: 4,
									flex: "0",
									textAlign: "center",
								}}
							>
								{t("contact")}
							</Box>
						</Box>
					</Grid>
					<Grid size={12}>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="name"
							label={t("company")}
							required
							error={!!errors?.name}
							fullWidth
							autoFocus
						/>
					</Grid>
					<Grid
						size={{
							lg: 7,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="street"
							label={t("street")}
							required
							error={!!errors?.street}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							lg: 5,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="city"
							label={t("city_label")}
							required
							error={!!errors?.city}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							lg: 3,
						}}
					>
						{isLoadingCountryCodes ? (
							<Skeleton
								width={401}
								height={36.71}
								sx={{
									transform: "scale(1)",
									marginTop: "16px",
									marginBottom: "8px",
								}}
							/>
						) : (
							<FlexControlledSelectSeparatedLabel
								control={control}
								margin="normal"
								name="country"
								label={t("country")}
								required
								defaultValue=""
								options={countryCodes.map((code) => {
									return { value: code, label: code };
								})}
							/>
						)}
					</Grid>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="postalCode"
							label={t("postal_code_label")}
							required
							error={!!errors?.postalCode}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							lg: 5,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="nip"
							label={t("nip_label")}
							required
							error={!!errors?.nip}
							fullWidth
						/>
					</Grid>
					<Grid size={12}>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="contactPersons"
							label={t("contact_persons")}
							required
							error={!!errors?.contactPersons}
							fullWidth
							multiline
							minRows={3}
						/>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<Button loading={isLoading} type="submit" variant="contained">
								{t("save")}
							</Button>
						</Box>
					</Grid>
				</Grid>
				<Grid
					container
					spacing={0}
					size={{
						lg: 6,
					}}
				>
					<Grid size={12}>
						<Box>
							<Button variant="contained" onClick={handleAppendNote}>
								{t("new_note")}
							</Button>
						</Box>
						<Box
							sx={{
								mt: 2,
								width: "calc(100% - 30px)",
								maxHeight: "500px",
								height: "510px",
								paddingRight: "30px",
								paddingLeft: "6px",
								py: "8px",
								border: "1px solid #BFBFBF",
								position: "relative",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									overflow: "scroll",
									paddingRight: "37px",
									paddingLeft: "6px",
									py: "8px",
									mr: "-30px",
								}}
							>
								{unsavedNotes.map((note) => {
									return (
										<Box
											key={note.id}
											sx={{ display: "flex", gap: "6px", alignItems: "center" }}
										>
											<Typography sx={{ flex: "0", whiteSpace: "nowrap" }}>
												{note.date}:
											</Typography>
											<TextField
												sx={{ flex: "1" }}
												type="text"
												size="small"
												variant="standard"
												fullWidth
												value={note.value}
												onChange={(e) =>
													handleNoteChange(note.id, e.target.value)
												}
												slotProps={{
													input: {
														endAdornment: savingNotes[note.id] && (
															<CircularProgress size={20} />
														),
													},
												}}
											/>
											<Button
												variant="contained"
												onClick={() => handleNoteSave(note.id)}
											>
												{t("save")}
											</Button>
										</Box>
									);
								})}
								{notesState.map((note) => {
									return (
										<Box
											key={note.value}
											sx={{ display: "flex", gap: "6px", alignItems: "start" }}
										>
											<Typography sx={{ flex: "0", whiteSpace: "nowrap" }}>
												{note.date}:
											</Typography>
											<Box
												sx={{
													flex: "1",
													display: "flex",
													alignItems: "center",
												}}
											>
												<Typography>{note.value}</Typography>
											</Box>
										</Box>
									);
								})}
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Grid>
        </Box>
    );
};

export default AddCompany;
